import api from '../api/instance';

import { User } from '@models/user.model';
import { Page } from '@testsigmainc/models';
import { ChangePasswordRequest } from '@models/change-password-request.model';
import { FeedbackModel } from '@navigation/models/feedback.model';
import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from 'react-query';
import { UserActivity } from '@models/user-activity.model';
import { USERS_QUERY_KEY } from '@modules/project-switcher/constants';
import { useReactQueryCaching } from '@hooks/use-react-query-caching';
import { CachingPrefix } from '@enums/caching-prefix.enum';
import { PrivateURLConstants } from '@constants/private-url.constant';

const nameSpace = PrivateURLConstants.USERS;
const pendingRequestsNameSpace = PrivateURLConstants.PENDING_REQUESTS;

const findAll = (query?: string, size?: number, page?: number) => {
  return api
    .get(nameSpace, {
      query,
      size,
      page,
    })
    .then((res) => new Page<User>().deserialize(res, User))
    .catch((e) => Promise.reject(e));
};

const findAllQuery = (
  query?: string,
  sort?: string,
  size?: number,
  page?: number,
) => {
  return api
    .get(nameSpace, { query, sort, size, page })
    .then((res) => new Page<User>().deserialize(res, User))
    .catch((e) => Promise.reject(e));
};
const findAllQuer2 = (
  query?: string,
  sort?: string,
  size?: number,
  page?: number,
) => {
  return api
    .get(nameSpace, { query, sort, size, page })
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};
export const useGetUsersInfinite = ({
  query,
  sort,
  size,
}: {
  query: string;
  sort?: string;
  size?: number;
}) =>
  useInfiniteQuery(
    [`users-${query}`, sort, size],
    async ({ pageParam = 0 }) => {
      const response = await findAllQuery(query, sort, size, pageParam);
      return response;
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage?.nextPage || false;
      },
      cacheTime: 0,
    },
  );

export const useGetUsers = (searchTerm) => {
  return useQuery({
    // The queryKey now includes 'page' because changing the page will trigger a re-fetch
    queryKey: ['users', searchTerm],
    queryFn: async () => {
      // Directly call findAllQuery with the provided 'page'
      const baseQuery = 'query=type!API_USER';
      const searchQuery = searchTerm ? `term:*${searchTerm}*` : '';
      const query = `${baseQuery},${searchQuery}`;
      const response = await findAllQuer2(query);
      return response;
    },
    // Optional: Add more useQuery options here.
    // For example, to only fetch if 'query' is provided:
    enabled: true,
    // Add cacheTime, staleTime, etc., as needed for a single query
    cacheTime: 5 * 60 * 1000, // Example: keep cached for 5 minutes
    staleTime: 60 * 1000, // Example: data is considered fresh for 1 minute
    // You typically wouldn't need getNextPageParam for useQuery as it's for infinite fetching.
  });
};
const show = (userId: number) => {
  return api
    .get(nameSpace + '/' + userId)
    .then((res) => new User().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const shareFeedback = (id: number, feedback: FeedbackModel) => {
  return api.put<void>(`${nameSpace}/${id}/feedback`, feedback);
};

const resendInvite = (id: number) => {
  return api
    .put<void>(`${nameSpace}/${id}/invite`, {})
    .then(() => {})
    .catch((e) => {
      throw e;
    });
};

const restoreUser = (id: number) => {
  return api
    .put(`${nameSpace}/${id}/restore`, {})
    .then(() => {})
    .catch((e) => {
      throw e;
    });
};

const deleteUser = (id: number) => {
  return api
    .remove(`${nameSpace}/${id}`, {})
    .then(() => {})
    .catch((e) => {
      throw e;
    });
};

const deleteUserData = (id: number) => {
  return api
    .remove(`${nameSpace}/${id}/data`, {})
    .then(() => {})
    .catch((e) => {
      throw e;
    });
};

const createUser = (user: User) => {
  return api
    .post(nameSpace, user)
    .then((res) => {
      return new User().deserialize(res);
    })
    .catch((e) => {
      throw e;
    });
};

const updateUser = (id: number, user: User) => {
  return api
    .put(`${nameSpace}/${id}`, user)
    .then((res) => {
      return new User().deserialize(res);
    })
    .catch((e) => {
      throw e;
    });
};

const status = (id: number, user: User) => {
  return api
    .put<void>(`${nameSpace}/${id}/status`, user)
    .then((res) => new User().deserialize(res))
    .catch((e) => {
      throw e;
    });
};

const editParallelCount = (id: number, user: User) => {
  return api
    .put(`${nameSpace}/${id}`, user)
    .then((res) => {
      return new User().deserialize(res);
    })
    .catch((e) => {
      throw e;
    });
};

const updateStatus = (id: number, user: User) => {
  return api
    .put<void>(`${nameSpace}/${id}/status`, user)
    .then(() => {})
    .catch((e) => {
      throw e;
    });
};

const notifyAdminByEmail = (id: number) => {
  return api
    .put<void>(`${nameSpace}/${id}/notifyAdmin`, {})
    .then(() => {})
    .catch((e) => {
      throw e;
    });
};

const updateUserPassword = (id: number, request: ChangePasswordRequest) => {
  return api
    .put<void>(`${nameSpace}/${id}/update_user_password`, request)
    .then(() => {})
    .catch((e) => {
      throw e;
    });
};

const getRecorderToken = (): Promise<{ token: string }> => {
  return api.get('/recorder-token');
};

const getAllUsers = (params?: Record<string, any>) => {
  return api
    .get(nameSpace, params)
    .then((res) => new Page<User>().deserialize(res, User))
    .catch((e) => {
      throw e;
    });
};

const getAllPendingRequests = (params?: Record<string, any>) => {
  return api
    .get(`${pendingRequestsNameSpace}/pending`, params)
    .then((res) => new Page<User>().deserialize(res, User))
    .catch((e) => Promise.reject(e));
};

const rejectPendingRequest = (id: number) => {
  return api
    .post(`${pendingRequestsNameSpace}/${id}/reject`, {})
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

export const useFetchUser = ({
  id,
  enabled,
}: {
  id: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ['user', id],
    queryFn: async () => await show(id),
    enabled,
  });
};

export const getAllUserForActivity = () => {
  const { getCachingTime } = useReactQueryCaching(
    CachingPrefix.FETCH_PROJECT_USERS_LIST,
    false,
  );
  return useQuery(
    CachingPrefix.FETCH_PROJECT_USERS_LIST,
    async () => {
      return await api
        .get(nameSpace + '/list')
        .then((res) => new Page<UserActivity>().deserialize(res, UserActivity))
        .catch((e) => {
          throw e;
        });
    },
    {
      cacheTime: getCachingTime(Infinity),
      staleTime: getCachingTime(Infinity),
    },
  );
};

export const UserService = {
  status,
  findAll,
  show,
  shareFeedback,
  resendInvite,
  restoreUser,
  deleteUser,
  deleteUserData,
  createUser,
  updateUser,
  editParallelCount,
  updateStatus,
  notifyAdminByEmail,
  updateUserPassword,
  getRecorderToken,
  getAllUsers,
  getAllPendingRequests,
  rejectPendingRequest,
};

const useRefetchUsersCache = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.refetchQueries(USERS_QUERY_KEY);
  };
};

export const useUsersFindAll = ({
  query,
  queryKey = [],
  enabled = true,
  onError = console.error,
  onSuccess = (res) => {},
}) => {
  return useQuery({
    queryKey: [USERS_QUERY_KEY, ...queryKey, query],
    queryFn: () => findAll(query, 50),
    enabled,
    onError,
    onSuccess,
  });
};

export const useCreateUserMutation = ({
  onSuccess = (data) => {},
  onError = console.error,
  onSettled = () => {},
}) => {
  const refetchUsersCache = useRefetchUsersCache();

  return useMutation(createUser, {
    onSuccess: (data) => {
      onSuccess(data);
      refetchUsersCache();
    },
    onError,
    onSettled,
  });
};

export const makeOrgOwner = (id: number) => {
  return api
    .put(`${nameSpace}/${id}/grant_ownership`, {})
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

export const revokeOrgOwner = (id: number) => {
  return api
    .put(`${nameSpace}/${id}/revoke_ownership`, {})
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};
